const {
	translateString
} = window.BAO.Utils

class CarouselElement extends window.BAO.CustomElement() {
	static get requiredElements() {
			return ['slider', 'slide']
	}

	async connectedCallback() {
		super.connectedCallback()

		this.carousel = await this.initialiseCarousel()

		requestAnimationFrame(() => {
			this.carousel.update()
		})

		setTimeout(() => {
			this.carousel.update()
		}, 100)

		document.addEventListener('mobile-search-state', e => {

			if (!e.detail?.open) return

			setTimeout(() => {

				if (this.carousel) {
					this.carousel.update()
				}

			}, 50)

		})
	}

	beforeInitialiseCarousel() {}

	async initialiseCarousel() {
			/* eslint-disable import/no-unresolved */
			const {
					default: KeenSlider
			} = await import(
					'https://cdn.skypack.dev/pin/keen-slider@v6.7.0-LKl0HtBlpKLwpmTQiIJc/mode=imports,min/optimized/keen-slider.js'
			)
			/* eslint-enable import/no-unresolved */
			this.beforeInitialiseCarousel()
			return new KeenSlider(this.els.slider.element, this.options, this.plugins)
	}

	/** @returns {KeenSliderOptions} */
	get options() {
			return {}
	}

	/** @returns {KeenSliderPlugins[]} */
	get plugins() {
			return []
	}
}

const setScrollable = () => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			carousel.on('detailsChanged', carousel => {
					let totalSlidesWidth = 0
					carousel.slides.forEach(slide => {
							totalSlidesWidth += parseInt(slide.offsetWidth, 10)
					})

					if (totalSlidesWidth > carousel.size) {
							carousel.container.setAttribute(`data-scrollable`, true)
					} else {
							carousel.container.setAttribute(`data-scrollable`, false)
					}
			})
	}
}

const setInitialised = () => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			carousel.on('created', carousel =>
					carousel.container.setAttribute(`data-initialised`, true),
			)
	}
}

/**
* @param {{
*  next?: HTMLButtonElement,
*  previous?: HTMLButtonElement,
* }} arrows
*/
const useArrows = ({
	next,
	previous
} = {}) => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			next?.addEventListener('click', carousel.next)
			previous?.addEventListener('click', carousel.prev)

			const updateButtonStates = () => {
					const {
							abs,
							maxIdx,
							min
					} = carousel.track.details

					if (next) {
							next.disabled = abs === maxIdx
					}

					if (previous) {
							previous.disabled = abs === min
					}
			}

			carousel.on('created', updateButtonStates)
			carousel.on('detailsChanged', updateButtonStates)
	}
}

/**
* @param {number} seconds
* @param {{
*  pauseOnHover?: boolean
* }} options
*/
const useAutoplay = (seconds = 2, {
	pauseOnHover = true
} = {}) => {
	const delay = seconds * 1000

	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			let timeout
			let mouseOver = false

			const clearCurrentTimout = () => clearTimeout(timeout)
			const setNextTimeout = () => {
					clearCurrentTimout()

					if (mouseOver) {
							return
					}

					timeout = setTimeout(carousel.next, delay)
			}

			carousel.on('created', () => {
					if (pauseOnHover) {
							carousel.container.addEventListener('mouseover', () => {
									mouseOver = true
									clearCurrentTimout()
							})
							carousel.container.addEventListener('mouseout', () => {
									mouseOver = false
									setNextTimeout()
							})
					}

					setNextTimeout()
			})

			carousel.on('dragStarted', clearCurrentTimout)
			carousel.on('animationEnded', setNextTimeout)
			carousel.on('updated', setNextTimeout)
	}
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			const updateDotStates = () => {
					dots.forEach((dot, index) => {
							const isCurrent = carousel.track.details.rel === index

							dot.setAttribute('aria-current', String(isCurrent))
					})
			}

			dots.forEach((dot, index) => {
					dot.addEventListener('click', () => {
							carousel.moveToIdx(index)
					})
			})

			carousel.on('created', updateDotStates)
			carousel.on('slideChanged', updateDotStates)
	}
}

/**
* @note Use with `renderMode: 'custom'` to ensure slides don't move
* and the following CSS
* ```css
* [your-selector].keen-slider {
*   display: grid;
* }
*
* [your-selector] .keen-slider__slide {
*   grid-area: 1 / -1;
* }
* ```
*/
const useFadeTransition = () => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			carousel.on('detailsChanged', () => {
					carousel.track.details.slides.forEach((slide, index) => {
							/** @type {HTMLElement} */
							const slideElement = carousel.slides[index]

							slideElement.style.setProperty('opacity', slide.portion)
					})
			})
	}
}

/** @param {HTMLElement} scrollbar */
const useScrollbar = scrollbar => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			const updateScrollbar = () => {
					let numberOfSlidesShown = 0

					const numberOfSlides = carousel.track.details.slides.length
					const currentIndex = carousel.track.details.rel

					if (numberOfSlides <= 1) {
							scrollbar.parentElement.remove()
					}

					carousel.track.details.slides.forEach(slide => {
							numberOfSlidesShown = numberOfSlidesShown + slide.portion
					})

					const scrollbarWidthPercentage =
							(numberOfSlidesShown / numberOfSlides) * 100

					const spaceToMove = 100 - scrollbarWidthPercentage

					let currentScrollPercentage = (currentIndex / numberOfSlides) * 100

					if (currentScrollPercentage > spaceToMove) {
							currentScrollPercentage = spaceToMove
					}

					if (scrollbar && scrollbar.style) {
							scrollbar.style.width = `${scrollbarWidthPercentage}%`
							scrollbar.style.left = `${currentScrollPercentage}%`
					}
			}

			carousel.on('created', updateScrollbar)
			carousel.on('slideChanged', updateScrollbar)
	}
}

/**
* @param {HTMLElement} indicator
* @param {template} string
*/
const useCurrentSlideIndicator = (indicator, template = '{{ current }}') => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			const updateIndicator = () => {
					const {
							rel,
							length
					} = carousel.track.details
					const current = String(rel + 1)
					const total = String(length + 1)

					indicator.textContent = translateString(template, {
							current,
							total,
					})
			}

			carousel.on('created', updateIndicator)
			carousel.on('slideChanged', updateIndicator)
	}
}

/**
* @todo Approach used instead of Keensliders
* 'origin' option as that results in sizing issues
* @param {number} index
*/
const useStartAtSlide = index => {
	/** @param {KeenSliderInstance} carousel */
	return carousel => {
			const moveToSlide = () => {
					carousel.moveToIdx(index)
			}

			carousel.on('created', moveToSlide)
	}
}

window.BAO.Carousel = window.BAO.Carousel || {
	CarouselElement,
}
window.BAO.Carousel.plugins = {
	setInitialised,
	setScrollable,
	useArrows,
	useAutoplay,
	useCurrentSlideIndicator,
	useDots,
	useFadeTransition,
	useScrollbar,
	useStartAtSlide,
}
